import { DatePicker as AntDatePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

import { AntThemed } from "@/components/ui/ant-theme";
import { cn } from "@/utils/utils";

/** The API's shape for a date, and the only shape this component hands back. */
const WIRE = "YYYY-MM-DD";
/** What a person reads, the same way every date on the screens is written. */
const SHOWN = "MM/DD/YYYY";
/** Everything a date in that format is made of, and nothing else. */
const TYPEABLE = /^[\d/]+$/;

/**
 * Ant Design's calendar, wearing this app's colours.
 *
 * The native `<input type="date">` is the operating system's control, not the
 * product's: a different calendar on every platform, its own type and its own
 * blue, and on some of them no keyboard path at all. This is one calendar
 * everywhere.
 *
 * The value stays a plain `YYYY-MM-DD` string on both sides — the form holds
 * strings and the API takes them — so nothing outside this file has to know
 * that a date library is involved.
 */
export function DatePicker({
  id,
  value,
  onChange,
  disabled = false,
  required = false,
  invalid = false,
  placeholder,
  className,
  min,
  max,
  describedBy,
}: {
  id?: string;
  /** `YYYY-MM-DD`, or "" for no date. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean | undefined;
  /**
   * Whether a date has to be given. antd's field has no validity of its own,
   * so it is carried by a hidden input beside it, the way the pickers do it.
   */
  required?: boolean | undefined;
  /**
   * Wrong for a reason the field cannot see in itself.
   *
   * A date can be a perfectly good date and still be refused — a start that
   * falls after its end is only wrong in the other's company. `Form` marks
   * what its own `rules` refuse, but it does that at the moments a form
   * speaks: on leaving a field, or on Save. A pair that crosses says so the
   * moment it crosses, which is the caller's judgement and arrives here.
   *
   * It lands on the picker's own input, which is what the red border follows
   * (see `.ant-picker:has(input[aria-invalid="true"])` in `index.css`) and
   * what `Form` writes to for its own refusals, so both read the same.
   */
  invalid?: boolean | undefined;
  placeholder?: string | undefined;
  className?: string | undefined;
  /** Earliest day the field will take, `YYYY-MM-DD`. */
  min?: string | undefined;
  /** Latest day the field will take, `YYYY-MM-DD`. */
  max?: string | undefined;
  /**
   * The id of whatever is said under the field — a duplicate, an age refused.
   * A `<p role="status">` beside the box is read by nobody unless the box
   * points at it.
   */
  describedBy?: string | undefined;
}) {
  const parsed = value ? dayjs(value, WIRE) : null;
  const host = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  /**
   * The clear button out of the tab order.
   *
   * antd draws it as a real `<button>`, so Tab off a date that has a value
   * lands on the little × rather than on the next field. It is antd's own
   * element, with no prop to reach it, so it is set here — beside every other
   * control in a form that is not a field of its own, which the pointer
   * reaches and Tab passes over.
   */
  /**
   * Set while a date picked from the calendar is settling.
   *
   * antd hands focus back to the field on a pick, and focus on this field is
   * one of the things that opens the panel — so a single click closed the
   * calendar and opened it again. While this is set, a request to open is
   * refused, whatever asked for it.
   */
  const picking = useRef(false);

  /**
   * The last date handed back, until the caller has taken it.
   *
   * Two paths report one: the blur below takes what was typed, and the
   * picker's own `onChange` fires straight after for the same date. The
   * caller's `value` cannot tell them apart — it is still the old one while
   * the state update is in flight — so the date just sent is remembered until
   * a new `value` arrives, and a second report of it is dropped.
   */
  const sent = useRef<string | null>(null);
  useEffect(() => {
    sent.current = null;
  }, [value]);

  /** Hand a date back once: not the one held, and not the one just sent. */
  const report = (wire: string) => {
    if (wire === value || wire === sent.current) return;
    sent.current = wire;
    onChange(wire);
  };

  /**
   * Whether the field was reached with the pointer, rather than with Tab.
   *
   * The calendar opens itself when the field takes focus, which on a Tab puts
   * it under wherever the pointer happens to be resting — and the day it
   * hovers is written into the field as a preview, over what is being typed.
   * See `onOpenChange`.
   */
  const byPointer = useRef(false);

  /**
   * Whether a date is being typed into the field.
   *
   * The picker asks to open on every change to the text, so closing the
   * calendar on a keystroke is undone by the next one unless the asking is
   * refused for as long as the typing lasts. Cleared by leaving the field, by
   * reaching for the calendar, and by Enter.
   */
  const typing = useRef(false);

  /**
   * The clear button out of the tab order.
   *
   * antd draws it as a real `<button>`, so Tab off a date that has a value
   * lands on the little × rather than on the next field. It is antd's own
   * element, with no prop to reach it, so it is set here — beside every other
   * control in a form that is not a field of its own, which the pointer
   * reaches and Tab passes over.
   */
  useEffect(() => {
    const button = wrap.current?.querySelector<HTMLElement>(".ant-picker-clear");
    if (button) button.tabIndex = -1;
  }, [value, open]);

  /**
   * The calendar goes in the browser's top layer, with the sheet.
   *
   * Every dialog in the app is a native `<dialog>` opened with `showModal()`,
   * which is painted above everything in the normal document whatever its
   * z-index — so a panel left at the end of `<body>` is behind the sheet, and
   * one put inside the sheet is cut off by its edge. The top layer is a stack
   * and the last element promoted sits on top, so the panel gets a host of its
   * own, promoted when it opens: above the sheet, and clipped by nothing.
   */
  useEffect(() => {
    const element = host.current;
    if (!element || !("showPopover" in element)) return;
    try {
      if (element.matches(":popover-open")) element.hidePopover();
      if (open) element.showPopover();
    } catch {
      // A browser with no top layer paints it the old way, which is fine.
    }
  }, [open]);

  return (
    <AntThemed>
      <div
        ref={wrap}
        className="contents"
        onPointerDown={() => {
          byPointer.current = true;
          typing.current = false;
        }}
        onBlur={() => {
          byPointer.current = false;
          typing.current = false;
          /**
           * What was typed is taken when the field is left, however it is left.
           *
           * The picker reports a date on Enter, on a pick, and on tabbing out,
           * but a pointer leaving for a button — Submit, most of the time — is
           * none of those: the text stays text, the value behind it never
           * moves, and the save goes out carrying the date that was there
           * before. Somebody typing a correction and pressing Save has said
           * what they mean twice.
           *
           * Only a whole date in this format, and only when it says something
           * the field is not already holding, so the path that does report one
           * does not report it twice.
           */
          const typed = wrap.current?.querySelector("input")?.value ?? "";
          const parsed = dayjs(typed, SHOWN, true);
          // A day the screen has ruled out is not filed by the back door: the
          // calendar greys it and the keyboard refuses it, and so does this.
          const outside =
            (!!min && parsed.isBefore(dayjs(min, WIRE), "day")) ||
            (!!max && parsed.isAfter(dayjs(max, WIRE), "day"));
          if (!parsed.isValid() || outside) return;
          report(parsed.format(WIRE));
        }}
        /**
         * Enter belongs to this component, not to the picker underneath it.
         *
         * Captured before the picker sees it, because the picker's own Enter
         * takes whatever text the field is showing — and an open calendar
         * shows the day under the pointer, not the date typed. Enter read
         * from there files a day nobody chose.
         *
         * Closed, Enter opens the calendar on the date typed: the text is
         * parsed and set first, so the day the calendar marks and the date in
         * the field are the same one. Open, Enter takes what is marked and
         * shuts it. Type, Enter to see it, Enter to file it.
         */
        onKeyDownCapture={(event) => {
          if (disabled || event.key !== "Enter") return;
          event.preventDefault();
          event.stopPropagation();

          if (!open) {
            const typed = wrap.current?.querySelector("input")?.value ?? "";
            const parsed = dayjs(typed, SHOWN, true);
            // Nothing written, nothing to show but today. Something written
            // that is not a date in this format — "19/11/2005", a day where
            // the month goes — has no day to open on, and opening on today's
            // month instead says the entry was understood when it was not.
            // The field is left marked wrong, which is the answer.
            if (typed && !parsed.isValid()) return;
            if (parsed.isValid()) {
              typing.current = false;
              report(parsed.format(WIRE));
            }
            setOpen(true);
            return;
          }

          // The day the calendar marks: what it was opened on, or today when
          // it was opened on nothing. The cell carries the date it stands for.
          const cell = host.current?.querySelector(
            ".ant-picker-cell-selected, .ant-picker-cell-today",
          );
          const day = cell?.getAttribute("title");
          // Confirming the day already held is not a change to report.
          if (day) report(day);
          setOpen(false);
        }}
        onPaste={(event) => {
          // Pasted text is held to the same shape as typed text.
          if (disabled) return;
          const pasted = event.clipboardData.getData("text");
          if (!TYPEABLE.test(pasted) || pasted.length > SHOWN.length) event.preventDefault();
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          /**
           * Only what a date in this format is made of.
           *
           * The field takes MM/DD/YYYY and nothing else, and the picker's way
           * of saying so is to empty the box on the way out — so a name or a
           * date written the other way round is typed in full, accepted in
           * appearance, and gone the moment the field is left. Refusing the
           * characters says it at the keystroke instead, while it can still
           * be acted on. Shortcuts and every key that is not a character of
           * its own are left alone.
           */
          if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const input = event.currentTarget.querySelector("input");
            const selected = (input?.selectionEnd ?? 0) - (input?.selectionStart ?? 0);
            const full = (input?.value.length ?? 0) - selected >= SHOWN.length;
            if (!TYPEABLE.test(event.key) || full) {
              event.preventDefault();
              return;
            }
          }
          /**
           * Typing shuts the calendar.
           *
           * An open calendar writes the day under the pointer into the field
           * as a preview, so a date typed with the pointer resting anywhere
           * over the panel is replaced, character for character, by a day
           * nobody chose. Someone typing a date is not reading the days;
           * Enter above is how they ask to see them. See `onOpenChange`.
           *
           * A printable key is the test: the arrows belong to the panel, and
           * Backspace is someone correcting what they typed, which is still
           * typing.
           */
          if (open && (event.key.length === 1 || event.key === "Backspace")) {
            typing.current = true;
            setOpen(false);
            return;
          }
          if (event.key.length === 1 || event.key === "Backspace") typing.current = true;
        }}
      >
        <AntDatePicker
          {...(id === undefined ? {} : { id })}
          {...(describedBy === undefined ? {} : { "aria-describedby": describedBy })}
          {...(invalid ? { "aria-invalid": true } : {})}
          className={cn("w-full", className)}
          size="large"
          allowClear
          inputReadOnly={false}
          // Plain text, not a mask.
          //
          // A masked field was tried, to stop the year completing itself out
          // of thin air while a birth date was being typed. It was the wrong
          // cure: the year came from the calendar previewing whatever day the
          // pointer was resting on — see `onOpenChange` — and the mask brought
          // its own trouble, filling digit by digit into fixed places and
          // losing what it had of a part between keystrokes, so "2000" typed
          // into the year settled as "0000".
          format={SHOWN}
          open={open}
          // A pick closes the calendar, and nothing reopens it until the field
          // is settled. antd hands focus back to the input on a pick, and
          // focus on this field is one of the things that opens the panel — so
          // closing and reopening was a single click. Refusing the open while
          // `picking` holds covers that whatever asks for it: the focus, the
          // caret's arrow keys, or the popover host being hidden under it.
          //
          // And Tab into the field leaves it shut. It opens itself on focus
          // otherwise, which puts a calendar under wherever the pointer is
          // resting — and the day it hovers is written into the field as a
          // preview, over what is being typed. That reads exactly as the
          // field picking a date on its own: a whole date, current year and
          // all, appearing on the first keystroke after a Tab. Reached with
          // the pointer it opens as before, and Enter opens it from the
          // keyboard — both are someone asking for the calendar.
          onOpenChange={(next) => {
            if (next && (picking.current || typing.current || !byPointer.current)) return;
            setOpen(next);
          }}
          getPopupContainer={() => host.current ?? document.body}
          // The host covers the screen so the panel can be placed anywhere on it,
          // so the host itself must catch nothing: a click beside the calendar
          // belongs to whatever is under it, which is also what closes the panel.
          classNames={{ popup: { root: "pointer-events-auto" } }}
          /**
           * No hover preview.
           *
           * With it on — the default — moving over a day writes that day into
           * the field, replacing the date typed there with whatever the
           * pointer happens to be near. It reverts on leaving, which is no
           * comfort: a date typed with the calendar open reads as the wrong
           * date, and Enter files what it reads. The calendar marks the date
           * held; the field shows the date typed; neither speaks for the
           * other.
           */
          /**
           * The days outside the bounds are not offered and not taken.
           *
           * A screen that says what it will accept — a birth date is not in
           * the future and not two centuries back — had been saying it to
           * nobody: the bounds reached the field and stopped there, so
           * "01/22/1111" was a date like any other. Greyed in the calendar,
           * and refused from the keyboard, which is where it gets typed.
           */
          disabledDate={(date) =>
            (!!min && date.isBefore(dayjs(min, WIRE), "day")) ||
            (!!max && date.isAfter(dayjs(max, WIRE), "day"))
          }
          previewValue={false}
          placeholder={placeholder ?? SHOWN.toLowerCase()}
          disabled={disabled}
          value={parsed !== null && parsed.isValid() ? parsed : null}
          onChange={(date) => {
            // Long enough to outlast the focus antd returns to the field,
            // short enough that a deliberate click straight after still opens
            // the calendar.
            picking.current = true;
            window.setTimeout(() => {
              picking.current = false;
            }, 250);
            setOpen(false);
            // A cleared field is a change like any other, and "" is never the
            // date just sent, so it goes straight out.
            if (date) report(date.format(WIRE));
            else onChange("");
          }}
        />
        {/* The form's validity, for a control that has none: empty and
            required refuses the save, and `data-validity-for` puts the
            message under the date field rather than under this. */}
        <input
          tabIndex={-1}
          aria-hidden
          className="sr-only"
          data-validity-for={id}
          required={required}
          disabled={disabled}
          value={value}
          onChange={() => undefined}
        />
      </div>
      <div
        ref={host}
        popover="manual"
        className="pointer-events-none fixed inset-0 m-0 size-full overflow-visible border-0 bg-transparent p-0"
      />
    </AntThemed>
  );
}
